import { useEffect, useMemo, useEffectEvent } from 'react';
import { useQuery } from '@apollo/client/react';
import { ErrorLike } from '@apollo/client';

import {
    IOfferEventEnrollmentManagementListItemFragment,
    IOfferEventEnrollmentSortField,
    IOfferEventSubscriptionStatus,
    IOfferEventType,
    ISortDirection,
    OfferEventEnrollmentManagementDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useEnrollmentsManagementFilters } from 'offer/hooks/enrollment/useEnrollmentsManagementFilters';
import { usePagination } from 'common/hooks/usePagination';
import { useSortBy } from 'common/hooks/useSortBy';

export const useEnrollmentsManagementList = (
    enrollmentStatus: IOfferEventSubscriptionStatus
) => {
    const { showApolloError } = useApolloError();
    const { sortBy, onSort } = useSortBy(
        IOfferEventEnrollmentSortField.StatusUpdatedOn,
        ISortDirection.Desc
    );

    const {
        loading: extraUserFieldsLoading,
        filters,
        filterValues,
        filterValuesForQuery,
        handleSearch,
        handleFilterChange,
    } = useEnrollmentsManagementFilters(enrollmentStatus);

    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const queryVariables = useMemo(
        () => ({
            ...filterValuesForQuery,
            status: enrollmentStatus,
            sortBy,
            eventTypes: [IOfferEventType.Meeting, IOfferEventType.Webinar],
            ...paginationVariables,
        }),
        [filterValuesForQuery, enrollmentStatus, sortBy, paginationVariables]
    );

    const exportQueryVariables = useMemo(
        () => ({
            ...filterValuesForQuery,
            eventTypes: [IOfferEventType.Meeting, IOfferEventType.Webinar],
            status: enrollmentStatus,
        }),
        [filterValuesForQuery, enrollmentStatus]
    );

    const {
        data,
        loading: enrollmentsLoading,
        error,
    } = useQuery(OfferEventEnrollmentManagementDocument, {
        variables: queryVariables,
    });

    const onError = useEffectEvent((error: ErrorLike) => {
        showApolloError(error);
    });

    useEffect(() => {
        if (error) {
            onError(error);
        }
    }, [error]);

    const enrollmentsObject = data?.manageableOfferEventEnrollments;
    const enrollmentsCount = enrollmentsObject?.count;
    const enrollments =
        (enrollmentsObject?.edges
            .map((edge) => edge?.node || null)
            .filter(
                Boolean
            ) as IOfferEventEnrollmentManagementListItemFragment[]) || [];

    const loading = extraUserFieldsLoading || enrollmentsLoading;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(enrollmentsCount || 0);
    }, [enrollmentsCount, loading, initializePagination]);

    return {
        enrollments,
        loading,
        currentSort: sortBy,
        filters,
        filterValues,
        paginationSettings,
        exportQueryVariables,
        onSearch: handleSearch,
        onFilterChange: handleFilterChange,
        onSort,
        setPage,
    };
};
